// Värmemodellen (b4 avsnitt 3 D): huset som en enda värmekapacitet, med en termostat.
//
//   C·dT/dt = P·u − UA·(T_inne − T_ute)
//
// P är värmens effekt (kW), UA förlusten mot ute (kW/K) och C värmekapaciteten (kWh/K). Värmen
// styrs av en termostat: under börvärdet går den med full effekt, och när börvärdet är nått
// håller den det med den effekt som förlusten kräver. Över börvärdet går ingen värme, och huset
// svalnar mot temperaturen ute, men aldrig under börvärdet. Komfort är ett högt börvärde, Eko och
// Borta ett lågt.
//
// Steget räknas exakt, med lösningen till ekvationen, inte med små tidssteg. Molnet planerar
// förvärmningen med det, och simulatorerna för Home Assistant och Nobø låter temperaturen inne
// följa värmen med samma modell.

export interface Varmeparametrar {
  /** Värmens effekt när den går fullt, P (kW). */
  effektKw: number;
  /** Förlusten mot ute per grad, UA (kW/K). */
  forlustKwPerGrad: number;
  /** Värmekapaciteten, C (kWh/K). */
  kapacitetKwhPerGrad: number;
}

export interface Varmesteg {
  /** Temperaturen inne efter steget. */
  temp: number;
  /** Energin som värmen använde under steget (kWh). */
  energiKwh: number;
}

/**
 * Ett steg på `timmar` timmar med termostaten på `borvarde`. Temperaturen ute är konstant under
 * steget. Resultatet växer med temperaturen före steget, aldrig tvärtom.
 */
export function varmesteg(
  temp: number,
  borvarde: number,
  ute: number,
  timmar: number,
  p: Varmeparametrar,
): Varmesteg {
  if (!(timmar > 0)) return { temp, energiKwh: 0 };
  const ua = p.forlustKwPerGrad;
  const a = ua / p.kapacitetKwhPerGrad;
  /** Mot `mal` under tiden t, från `fran`. */
  const mot = (fran: number, mal: number, t: number) => mal + (fran - mal) * Math.exp(-a * t);
  const hall = Math.max(0, ua * (borvarde - ute));

  if (temp < borvarde) {
    // Full effekt mot jämvikten, tills börvärdet är nått.
    const jamvikt = ute + p.effektKw / ua;
    if (jamvikt <= borvarde) {
      return { temp: mot(temp, jamvikt, timmar), energiKwh: p.effektKw * timmar };
    }
    const tillBorvardet = Math.log((jamvikt - temp) / (jamvikt - borvarde)) / a;
    if (tillBorvardet >= timmar) {
      return { temp: mot(temp, jamvikt, timmar), energiKwh: p.effektKw * timmar };
    }
    const kvar = timmar - tillBorvardet;
    if (ute > borvarde) {
      // Varmare ute än börvärdet: huset fortsätter att bli varmare, utan värme.
      return { temp: mot(borvarde, ute, kvar), energiKwh: p.effektKw * tillBorvardet };
    }
    return { temp: borvarde, energiKwh: p.effektKw * tillBorvardet + hall * kvar };
  }

  // Vid eller över börvärdet: ingen värme, tills huset har svalnat till börvärdet.
  if (ute >= borvarde) return { temp: mot(temp, ute, timmar), energiKwh: 0 };
  const tillBorvardet = temp > borvarde ? Math.log((temp - ute) / (borvarde - ute)) / a : 0;
  if (tillBorvardet >= timmar) return { temp: mot(temp, ute, timmar), energiKwh: 0 };
  return { temp: borvarde, energiKwh: hall * (timmar - tillBorvardet) };
}

/** En kvart, i millisekunder. Priserna, prognosen och planen räknas i kvartar. */
export const KVART_MS = 15 * 60_000;

/** Kvartens början för en tidpunkt. */
export function kvartenFore(ms: number): number {
  return Math.floor(ms / KVART_MS) * KVART_MS;
}

/** Nästa kvartsgräns, eller tidpunkten själv om den är en kvartsgräns. */
export function kvartenEfter(ms: number): number {
  return Math.ceil(ms / KVART_MS) * KVART_MS;
}

/**
 * b13: vinden i värmemodellen. Förlusten mot ute ökar med vinden, här med 3 % per m/s upp till
 * 15 m/s (luften byts snabbare och väggarnas yta kyls mer). Det räknas som en lägre temperatur
 * ute med samma förlust, så att modellen och skattningen inte ändras. Utan vind blir det
 * temperaturen ute, som förut.
 */
export const VINDFAKTOR = 0.03;

export function uteMedVind(ute: number, vindMs: number | null, inne = 20): number {
  if (vindMs === null || !(vindMs > 0)) return ute;
  return ute - VINDFAKTOR * Math.min(vindMs, 15) * Math.max(0, inne - ute);
}
